import React, { Dispatch } from 'react';
import { AgGridReact } from 'ag-grid-react';
import * as Bixby from '$/core';
import dayjs from 'dayjs';
import NoDataFound from '$/components/global/noData';
import { GridApi } from 'ag-grid-enterprise';

interface ITransactionCompsGridProps {
  gridData?: Bixby.Models.CompanyTransactionComp[];
  isAdmin: boolean;
  setGridRef?: Dispatch<GridApi> | null;
  linkedGrid?: GridApi | null;
}

export const TransactionCompsGrid: React.FC<ITransactionCompsGridProps> = props => {
  const { gridData, isAdmin, setGridRef, linkedGrid } = props;

  const formatCurrency = (value: number | null | undefined, includeCents: boolean) => {
    if (value === null || value === undefined) {
      return null;
    }

    const parsedValue: number = parseFloat(value as any);
    if (isNaN(parsedValue)) {
      return null;
    }

    // add in appropriate formatting
    let prefix = '$';
    let suffix = '';
    if (parsedValue < 0) {
      prefix = prefix + '(';
      suffix = suffix + ')';
    }

    // remove hyphen
    const fixedParsedValue = parsedValue.toFixed(2).replace('-', '');

    let currency: string = fixedParsedValue.replace(/\d(?=(\d{3})+\.)/g, '$&,');

    if (!includeCents) {
      currency = currency.split('.')[0];
    }

    return prefix + currency + suffix;
  };

  const formatPercentage = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return null;
    }

    const parsedValue: number = parseFloat(value as any);
    if (isNaN(parsedValue)) {
      return null;
    }

    const percentage: string = (parsedValue * 100).toFixed(1);
    return percentage + '%';
  };

  const formatMultiplier = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return null;
    }

    const parsedValue: number = parseFloat(value as any);
    if (isNaN(parsedValue)) {
      return null;
    }

    const multiplier: string = parsedValue.toFixed(1);
    return multiplier + 'x';
  };

  const getContextMenuItems = (params: any) => {
    if (params.column.colId === 'uniqueName' || params.column.colId === 'companyName') {
      return [];
    }

    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  const onGridReady = (params: any) => {
    if (setGridRef) {
      setGridRef(params.api || null);
    }
  };

  if (gridData && !Bixby.Utils.isNullOrEmpty(gridData)) {
    return (
      <React.Fragment>
        <div className="grid_container">
          <div className="grid ag-theme-alpine">
            <AgGridReact
              enableCharts={true}
              enableRangeSelection={true}
              getContextMenuItems={getContextMenuItems}
              defaultColDef={{
                flex: 1,
                minWidth: 150,
                sortable: true,
                resizable: true,
                lockPinned: true,
                filter: 'agNumberColumnFilter',
                cellClass: 'align-center',
              }}
              rowData={gridData}
              suppressDragLeaveHidesColumns={true}
              suppressMovableColumns={true}
              animateRows={true}
              rowHeight={32}
              headerHeight={32}
              onGridReady={onGridReady}
              alignedGrids={[{ api: linkedGrid }]}
              columnDefs={[
                {
                  field: 'uniqueName',
                  filter: 'agTextColumnFilter',
                  floatingFilter: true,
                  headerName: 'Target',
                  cellRenderer: (params: any) => {
                    const url = '/company/' + params.data['companyId'];
                    return (
                      <a className="link_underline" href={url}>
                        {' '}
                        {params.data['uniqueName']}{' '}
                      </a>
                    );
                  },
                  minWidth: 250,
                  pinned: 'left',
                  cellClass: 'align-left',
                  hide: !isAdmin,
                },
                {
                  field: 'companyName',
                  filter: 'agTextColumnFilter',
                  floatingFilter: true,
                  headerName: 'Target',
                  cellRenderer: (params: any) => {
                    const url = '/company/' + params.data['companyId'];
                    return (
                      <a className="link_underline" href={url}>
                        {params.data['companyName']}
                      </a>
                    );
                  },
                  minWidth: 250,
                  pinned: 'left',
                  cellClass: 'align-left',
                  hide: isAdmin,
                },
                {
                  field: 'industryGroupName',
                  filter: 'agTextColumnFilter',
                  floatingFilter: true,
                  headerName: 'Industry Group',
                  minWidth: 250,
                  cellClass: 'align-left',
                },
                {
                  field: 'ownership',
                  filter: 'agTextColumnFilter',
                  floatingFilter: true,
                  headerName: 'Buyer',
                  minWidth: 250,
                  cellClass: 'align-left',
                },
                {
                  field: 'transactionDate',
                  headerName: 'Date',
                  valueGetter: (params: any) => {
                    if (params.data['transactionDate']) {
                      return Bixby.Utils.formatMonthYear(dayjs.utc(params.data['transactionDate']).toDate()) as any;
                    }
                    return '';
                  },
                  minWidth: 100,
                  filter: 'agTextColumnFilter',
                },
                {
                  field: 'totalEnterpriseValue',
                  headerName: 'TEV',
                  valueFormatter: (params: any) => {
                    const result = formatCurrency(params.value, false);
                    return result || 'NA';
                  },
                  minWidth: 100,
                },
                {
                  headerName: 'Current Loan-to-Value(1)',
                  children: [
                    {
                      field: 'firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda',
                      headerName: '1st Lien',
                      valueFormatter: (params: any) => {
                        const result = formatPercentage(params.value);
                        return result || 'NM';
                      },
                    },
                    {
                      field: 'totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda',
                      headerName: 'Total',
                      valueFormatter: (params: any) => {
                        const result = formatPercentage(params.value);
                        return result || 'NM';
                      },
                    },
                  ],
                },
                {
                  headerName: 'TEV /',
                  children: [
                    {
                      field: 'totalEnterpriseValueByRevenue',
                      headerName: 'Revenue',
                      valueFormatter: (params: any) => {
                        const result = formatMultiplier(params.value);
                        return result || 'NM';
                      },
                    },
                    {
                      field: 'totalEnterpriseValueByAdjustedManagementEbitda',
                      headerName: 'Adj. EBITDA',
                      valueFormatter: (params: any) => {
                        const result = formatMultiplier(params.value);
                        return result || 'NM';
                      },
                    },
                  ],
                },
                {
                  headerName: 'Financials at transaction',
                  children: [
                    {
                      field: 'revenueAtBuyout',
                      headerName: 'Revenue',
                      valueFormatter: (params: any) => {
                        const result = formatCurrency(params.value, false);
                        return result || 'NA';
                      },
                    },
                    {
                      field: 'managementAdjustedEbitdaAtBuyout',
                      headerName: 'Adj. EBITDA',
                      valueFormatter: (params: any) => {
                        const result = formatCurrency(params.value, false);
                        return result || 'NA';
                      },
                    },
                  ],
                },
                {
                  headerName: 'Capital Structure at transaction',
                  children: [
                    {
                      field: 'transactionDetailsSecuredDebt',
                      headerName: 'Gross Debt',
                      valueFormatter: (params: any) => {
                        const result = formatCurrency(params.value, false);
                        return result || 'NA';
                      },
                    },
                    {
                      field: 'transactionDetailsCash',
                      headerName: 'Cash',
                      valueFormatter: (params: any) => {
                        const result = formatCurrency(params.value, false);
                        return result || 'NA';
                      },
                    },
                    {
                      field: 'netDebt',
                      headerName: 'Net Debt',
                      valueFormatter: (params: any) => {
                        const result = formatCurrency(params.value, false);
                        return result || 'NA';
                      },
                    },
                    {
                      field: 'netDebtPercentageOfCap',
                      headerName: '% of Cap',
                      valueFormatter: (params: any) => {
                        const result = formatPercentage(params.value);
                        return result || 'NM';
                      },
                    },
                    {
                      field: 'totalEquity',
                      headerName: 'Total Equity',
                      valueFormatter: (params: any) => {
                        const result = formatCurrency(params.value, false);
                        return result || 'NA';
                      },
                    },
                    {
                      field: 'totalEquityPercentageOfCap',
                      headerName: '% of Cap',
                      valueFormatter: (params: any) => {
                        const result = formatPercentage(params.value);
                        return result || 'NM';
                      },
                    },
                  ],
                },
                {
                  headerName: 'Change since transaction',
                  children: [
                    {
                      field: 'cumulativeDividends',
                      headerName: 'Dividends',
                      valueFormatter: (params: any) => {
                        const result = formatCurrency(params.value, false);
                        return result || 'NA';
                      },
                    },
                    {
                      field: 'impliedEquityAtRisk',
                      headerName: `Equity "at risk"`,
                      valueFormatter: (params: any) => {
                        const result = formatCurrency(params.value, false);
                        return result || 'NM';
                      },
                    },
                  ],
                },
              ]}></AgGridReact>
          </div>
        </div>
        <p className="transaction-comp-note">
          Note: please see Bixby company page and financial model for important notes and additional transaction
          information; equity "at risk" may include rollover equity.
        </p>
        <p className="transaction-comp-note">
          (1) Calculated as current 1st Lien (Total) Net Leverage divided by TEV / Adj. EBITDA at the time of
          transaction.
        </p>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <section>
          <div className="portfolio_no_data">
            <NoDataFound dataType="transaction comps" />
          </div>
        </section>
      </React.Fragment>
    );
  }
};
